import React, { useCallback, useEffect, useState, useMemo } from 'react';

import AgendaTabNav from './components/AgendaTabNav/AgendaTabNav';
import Card from './components/AgendaContent/Card';
import CardSeries from './components/AgendaContent/CardSeries';
import PromoCard from './components/AgendaContent/PromoCard';
import BundleCard from './components/AgendaContent/BundleCard';
import EmptyAgenda from './components/AgendaContent/EmptyAgenda';
import FilterbarMob from './components/AgendaFilters/FilterbarMob';
import Loader from './components/AgendaContent/Loader';
import Pagination from './components/Pagination';
import SkeletonLoader from './components/AgendaContent/SkeletonLoader';
import { filterTags, filterCategories } from './components/AgendaFilters/FilterbarMob';
import { today, getWeekNumber, sortByDate, hasMatchingShowTags, dbDateToTimestamp, getCookie, setCookie } from '../agenda/utils/helpers';

/**
 * @description      Core functionality for filtering the agenda
 */
const Filter = ({
	config,
	eventFeed,
	promoData,
	bundleData,
	availableTabs,
	availableLocations,
	availableDates,
	availableTags,
	availableCategories,
	//
	rootElement,
}) => {
	const [filteredElements, setFilteredElements] = useState([]); //result of the filtered data
	const [paginatedElements, setPaginatedElements] = useState([]); //result of the filtered data.
	const [filteredCount, setFilteredCount] = useState(null);
	const [cards, setCards] = useState([]); //state that holds the rendered components
	const [rendering, setRendering] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [showModal, setShowModal] = useState(false);
	const [categorySelected, setCategorySelected] = useState(false);

	//state reflecting params that tweak the filtering
	const [activeTab, setActiveTab] = useState('all_events'); //'all events = default), series', 'just-confirmed'
	const activeClassName = `${activeTab === 'series' ? 'series' : 'agenda'}__tiles`;

	const [activeFilterDate, setActiveFilterDate] = useState(false); //boolean (false = 'alle dagen'; true = checkout activeTimeStamp)
	const [activeTimestamp, setActiveTimeStamp] = useState(today); //becomes a timestamp when date filtering is used (Only one, not an array)

	const [dateRange, setDateRange] = useState(null); //'month' or 'week' or 'weekend'
	const [activeLocations, setActiveLocations] = useState([]); //empty array or one or more cinema as filter
	const [activeTags, setActiveTags] = useState([]);

	//
	const [activeCategories, setActiveCategories] = useState([]);

	// memo's to avoid unnecessary rerender filterbar
	const memoizedFilteredCount = useMemo(() => filteredCount, [filteredCount]);
	const memoizedActiveFilterDate = useMemo(() => activeFilterDate, [activeFilterDate]);
	const memoizedActiveTimestamp = useMemo(() => activeTimestamp, [activeTimestamp]);
	const memoizedDateRange = useMemo(() => dateRange, [dateRange]);
	const memoizedActiveLocations = useMemo(() => activeLocations, [activeLocations]);
	// const memoizedActiveTabs = useMemo(() => activeTabs, [activeTabs]); //??
	//
	const memoizedActiveCategories = useMemo(() => activeCategories, [activeCategories]);
	const memoizedActiveTags = useMemo(() => activeTags, [activeTags]);

	const headerRef = React.useRef(null);

	const scrollToTop = (isPagination) => {
		if (headerRef.current) {
			const offsetTop = headerRef.current.offsetTop;
			// Controleer of de agenda-items zichtbaar zijn
			if (isPagination || window.scrollY < offsetTop) {
				window.scrollTo({ top: offsetTop - 150, behavior: 'smooth' });
			}
		}
	};

	// Replace the current updateUrl function with this improved version
const updateUrl = useCallback(() => {
   const urlParams = new URLSearchParams(window.location.search); // Keep existing parameters

   // Clear parameters first
   if (!activeFilterDate) {
      urlParams.delete('f-day');
   } else {
      urlParams.set('f-day', encodeURI(activeTimestamp.toString()));
   }

   if (dateRange) {
      urlParams.set('f-date-range', encodeURI(dateRange.toString()));
   } else {
      urlParams.delete('f-date-range');
   }

   if (activeLocations.length > 0) {
      urlParams.set('f-location', encodeURI(activeLocations.map((location) => Object.keys(location)).join('+')));
   } else {
      urlParams.delete('f-location');
   }

   if (activeTags.length > 0) {
      urlParams.set('f-tag', encodeURI(activeTags.map((tag) => Object.keys(tag)).join('+')));
   } else {
      urlParams.delete('f-tag');
   }

   if (activeCategories.length > 0) {
      urlParams.set('f-category', encodeURI(activeCategories.join('+')));
   } else {
      urlParams.delete('f-category');
   }

   if (activeTab && activeTab !== 'all_events') {
      urlParams.set('f-tab', encodeURI(activeTab));
   } else {
      urlParams.delete('f-tab');
   }

   if (currentPage && currentPage > 1) {
      urlParams.set('f-page', encodeURI(currentPage.toString()));
   } else {
      urlParams.delete('f-page');
   }

   // Update URL
   if (urlParams.toString().length > 0) {
      window.history.replaceState(null, '', '?' + urlParams.toString());
   } else {
      window.history.replaceState(null, '', window.location.pathname);
   }
}, [activeFilterDate, activeTimestamp, dateRange, activeLocations, activeTags, activeCategories, activeTab, currentPage]);

	const tags = filterTags(memoizedActiveTags);
	const categories = filterCategories(memoizedActiveCategories);

	// Favourites / wishlist:
	const favouritesCookie = getCookie('mge_wishlist');
	const favouritesCookieArray = favouritesCookie ? decodeURIComponent(favouritesCookie).split(',').map(Number) : [];

	const [allFavourites, setAllFavourites] = useState(favouritesCookieArray);

	const toggleFavourite = (itemId) => {
		// Check if itemId is already in allFavourites:
		if (allFavourites.includes(itemId)) {
			setAllFavourites((prevFavourites) => prevFavourites.filter((id) => id !== itemId));
		} else {
			const newFavourites = [...allFavourites, itemId];
			setAllFavourites([...new Set([...favouritesCookieArray, ...newFavourites])]);
		}
	};

	useEffect(() => {
		// Set the cookie with allFavourites:
		setCookie('mge_wishlist', allFavourites.join(','), 7);
	}, [allFavourites]);

	/**
      * @description    Set params according to tab choice
      * @param {string} newTab - 'now', 'kids', 'expected', 'specials' or 'all'
      * @param {string} newTab - 'all_events', 'series', 'just_confirmed' or 'all'

   */
	const clickTabBar = (newTab) => {
		// First, reset date filters based on the new tab
		if (newTab === 'series' || newTab === 'just_confirmed' || newTab === 'all') {
			 setActiveFilterDate(false);
			 setActiveTimeStamp(null);
			 setDateRange(null);
		} else if (newTab === 'all_events') {
			 // For all_events, set today's date
			 setActiveFilterDate(true);
			 setActiveTimeStamp(today);
			 setDateRange(null);
		}

		// Reset to first page
		setCurrentPage(1);

		// Reset tags and categories for all tabs
		setActiveTags([]);
		setActiveCategories([]);

		// We're just changing the tab without filtering by tags/categories initially
		// The user can apply specific filters after tab selection if needed

		// Set the active tab
		setActiveTab(newTab);

		// Scroll to top
		scrollToTop(false);
  };

	const setDateParam = (value) => {
		if (value === 'all') {
			setActiveFilterDate(false);
			setActiveTimeStamp(null);
		} else {
			setActiveFilterDate(true);
			setActiveTimeStamp(value);
		}
	};

	const setDateRangeParam = (value) => {
		if (['month', 'week', 'weekend', null].includes(value)) setDateRange(value);
	};

	const setLocationsParam = (value) => {
		if (activeLocations.includes(value)) {
			setActiveLocations([...activeLocations].filter((locationOption) => Object.keys(locationOption)[0] !== Object.keys(value)[0]));
		} else {
			setActiveLocations([...activeLocations].concat(value));
		}
	};

	const setTagsParam = useCallback(
		(value) => {
			if (activeTags.includes(value)) {
				setActiveTags(activeTags.filter((tag) => tag !== value));
			} else {
				setActiveTags([...activeTags, value]);
			}
			setShowModal(false);
		},
		[activeTags]
	);

	const setCategoriesParam = useCallback(
		(value) => {
			if (activeCategories.includes(value)) {
				setActiveCategories(activeCategories.filter((category) => category !== value));
			} else {
				setActiveCategories([...activeCategories, value]);
			}
		},
		[activeCategories]
	);

	const resetParams = (resetAll = true) => {
		if (resetAll) {
			setActiveFilterDate(false);
			setActiveTimeStamp(today);
			setDateRange(null);
			setActiveLocations([]);
			setActiveCategories([]);

			setCurrentPage(1);
		}
		setActiveTags([]);
		setActiveCategories([]);
		setShowModal(false);

		setCategorySelected(false);

		window.history.replaceState(null, '', window.location.pathname);
	};

	const handlePageChange = (page) => {
		setCurrentPage(page);
		updateUrl();
		scrollToTop(true);
	};

	/**
	 * @description   Main filtering functionality, activated by a change in params through useEffect below
	 */
	/**
	 * @description   Main filtering functionality, activated by a change in params through useEffect below
	 */
	const filterAccordingToCurrentParams = useCallback(
		(input, bundles) => {
			// Als we in de 'series' tab zijn, filteren we de bundles in plaats van events
			if (activeTab === 'series') {
				let filteredBundles = structuredClone(Object.values(bundles));

				// OR filtering: bundles that match either tags OR categories
				if (activeTags.length > 0 || activeCategories.length > 0) {
					filteredBundles = filteredBundles.filter((bundle) => {
						let matchesFilter = false;

						// Check if bundle matches any selected tags
						if (activeTags.length > 0) {
							for (let tag of activeTags) {
								const tagKey = Object.keys(tag)[0];
								if (bundle.tags && Object.keys(bundle.tags).includes(tagKey)) {
									matchesFilter = true;
									break;
								}
							}
						}

						// Check if bundle matches any selected categories
						if (activeCategories.length > 0) {
							const main_genre = Object.values(bundle?.main_genre)[0];
							if (activeCategories.includes(main_genre)) {
								matchesFilter = true;
							}
						}

						return matchesFilter;
					});
				}

				return filteredBundles;
			}

			// Originele logica voor het filteren van events
			let copyInput = structuredClone(input);

			// OR filtering: elements that match either tags OR categories
			let resultCombinedFilter = [];
			if (activeTags.length > 0 || activeCategories.length > 0) {
				copyInput.forEach((element) => {
					let matchesFilter = false;

					// Check if element matches any selected tags
					if (activeTags.length > 0) {
						for (let tag of activeTags) {
							const tagKey = Object.keys(tag)[0];
							if (Object.keys(element.tags).indexOf(tagKey) !== -1) {
								matchesFilter = true;
								break;
							}
						}
						// Also check show tags if no direct tag match
						if (!matchesFilter) {
							const matchingShowTags = hasMatchingShowTags(element, config.no_special_tab_tags);
							if (matchingShowTags) {
								matchesFilter = true;
							}
						}
					}

					// Check if element matches any selected categories
					if (activeCategories.length > 0) {
						const main_genre = Object.values(element?.main_genre)[0];
						if (activeCategories.includes(main_genre)) {
							matchesFilter = true;
						}
					}

					if (matchesFilter) {
						resultCombinedFilter.push(element);
					}
				});
			} else {
				resultCombinedFilter = structuredClone(copyInput);
			}

			// filtering elements based on date AND return filtered times array for date
			let resultFilterDate = [];
			if (activeFilterDate && activeTimestamp) {
				resultCombinedFilter.forEach((element) => {
					const elementCopy = structuredClone(element);
					if (config.date_filter_range && dateRange) {
						if (dateRange === 'month') {
							elementCopy.times = elementCopy.times.filter((show) => new Date(show.date).getMonth() === new Date(activeTimestamp).getMonth());
						} else if (dateRange === 'week') {
							elementCopy.times = elementCopy.times.filter((show) => getWeekNumber(show.date) === getWeekNumber(activeTimestamp));
						} else if (dateRange === 'weekend') {
							elementCopy.times = elementCopy.times.filter((show) => {
								if (getWeekNumber(show.date) !== getWeekNumber(activeTimestamp)) return show; // future weeks
								const showDay = new Date(show.date).getDay();
								if (showDay === 5 || showDay === 6 || showDay === 0) return show; // this week & weekend (Friday, Saturday, Sunday)
							});
						}
					} else {
						if (config.results_beyond_date) {
							// Flint
							elementCopy.times = elementCopy.times.filter((show) => show.date >= activeTimestamp);
						} else {
							//Bioscopen
							elementCopy.times = elementCopy.times.filter((show) => show.date === activeTimestamp);
						}
					}
					resultFilterDate.push(elementCopy);
				});
			} else {
				resultFilterDate = structuredClone(resultCombinedFilter);
			}

			// filtering elements on location (only adjust shows array)
			let resultFilterLocations = [];
			if (activeLocations.length > 0) {
				resultFilterDate.forEach((element) => {
					const elementCopy = structuredClone(element);
					elementCopy.times = elementCopy.times.filter((show) => activeLocations.some((location) => Object.keys(location)[0] === Object.keys(show.location)[0]));
					resultFilterLocations.push(elementCopy);
				});
			} else {
				if (activeTab === 'just_confirmed') {
					resultFilterLocations = structuredClone(sortByDate(resultFilterDate, false, true));
				} else {
					resultFilterLocations = structuredClone(sortByDate(resultFilterDate, true, false));
				}
			}
			return resultFilterLocations;
		},
		[activeFilterDate, activeTimestamp, activeLocations, activeCategories, activeTags, activeTab, config, dateRange]
	);

	/**
	 * @description   Main filter (reacting to a change in params)
	 */

	useEffect(() => {
		if (!eventFeed) return;

		const filteredEvents = filterAccordingToCurrentParams(
			sortByDate(
				eventFeed.filter(({ times }) => times && times.length > 0),
				false,
				true
			),
			bundleData
		);

		const eventsWithTimes = filteredEvents.filter(({ times }) => times && times.length > 0);
		setFilteredCount(eventsWithTimes.length);

		// Define newFilteredElements here
		const newFilteredElements = activeTab === 'series' ? filteredEvents : eventsWithTimes;

		// Only update filteredElements if it has changed
		if (JSON.stringify(filteredElements) !== JSON.stringify(newFilteredElements)) {
			setFilteredElements(newFilteredElements);
			scrollToTop(false);
		}

		// Reset page to 1 whenever filters change
		setCurrentPage(1);
	}, [eventFeed, activeFilterDate, activeTimestamp, activeLocations, activeTags, activeTab, activeCategories, filterAccordingToCurrentParams]);

	/**
	 * @description   Renders the ReactElement cards based on the filteredElements
	 */
	useEffect(() => {
		setRendering(true);

		const startIndex = (currentPage - 1) * config.pagination_pagesize;
		const endIndex = startIndex + config.pagination_pagesize;

		const paginatedElements = filteredElements.slice(startIndex, endIndex);
		setPaginatedElements(paginatedElements);

		if (config.promo_cards_frequency && promoData && promoData.length > 0) {
			const copyPromoData = structuredClone(promoData);
			let index = config.promo_cards_frequency;
			while (index < cards.length) {
				cards.splice(
					index,
					0,
					<div className="promo-container-agenda" onClick={() => setShowModal(false)}>
						<PromoCard key={index} values={copyPromoData[0]} animationDelay={{ '--animation-delay': `${(index < 4 ? index + 1 : 4) * 100}ms` }} />
					</div>
				);
				index += config.promo_cards_frequency;
				copyPromoData.push(copyPromoData.shift());
			}
		}

		if (bundleData && bundleData.length > 0) {
			const copyBundleData = structuredClone(bundleData);
			let index = config.promo_cards_frequency;
			while (index < cards.length) {
				cards.splice(
					index,
					0,
					<div className="promo-container-agenda" onClick={() => setShowModal(false)}>
						<BundleCard key={index} animationDelay={{ '--animation-delay': `${(index < 4 ? index + 1 : 4) * 100}ms` }} />
					</div>
				);
				index += config.promo_cards_frequency;
				copyBundleData.push(copyBundleData.shift());
			}
		}

		setCards(cards);
		setRendering(false);
	}, [activeFilterDate, activeTimestamp, config, filteredElements, setTagsParam, currentPage, activeTab]);

	//toevoegen

	/**
	 * @description   Writes the current parameters to the url
	 */
	useEffect(() => {
		if (!rendering) updateUrl();
	}, [activeFilterDate, activeTab, activeTimestamp, activeLocations, activeTags, activeCategories, rendering, updateUrl, currentPage]);

	/**
	 * @description   Gets the parameters from url and sets the state accordingly
	 */
	useEffect(() => {
		// console.log('useEffect executed');
		if (eventFeed && window.location.search) {
			const queryParams = new URLSearchParams(window.location.search);
			//  console.log('queryParams:', queryParams.toString());

			if (queryParams.has('f-page')) {
				const pageParam = parseInt(decodeURI(queryParams.get('f-page') ?? '1'), 10);
				if (pageParam !== currentPage) {
					setCurrentPage(pageParam);
				}
			}

			if (queryParams.has('f-date-range')) {
				const param = decodeURI(queryParams.get('f-date-range') ?? '');
				if (['month', 'week', 'weekend'].includes(param)) setDateRange(param);
			}

			if (queryParams.has('f-day')) {
				const param = decodeURI(queryParams.get('f-day') ?? '');
				setActiveFilterDate(true);
				setActiveTimeStamp(Number(param));
			}

			if (queryParams.has('f-location')) {
				const slugs = decodeURI(queryParams.get('f-location') ?? '').split('+');
				const params = availableLocations.filter((location) => slugs.includes(Object.keys(location)[0]));
				if (params.length > 0) setActiveLocations(params);
			}

			if (queryParams.has('f-tag')) {
				const slugs = decodeURI(queryParams.get('f-tag') ?? '').split('+');
				const params = availableTags.filter((tag) => slugs.includes(Object.keys(tag)[0]));
				if (params.length > 0) setActiveTags(params);
			}

			if (queryParams.has('f-category')) {
				const slugs = decodeURI(queryParams.get('f-category') ?? '').split('+');
				const params = availableCategories.filter((category) => slugs.includes(category));
				if (params.length > 0) setActiveCategories(params);
			}

			if (queryParams.has('f-tab')) {
				const param = decodeURI(queryParams.get('f-tab') ?? '');
				setActiveTab(param);
			}
		}
	}, [eventFeed, availableLocations, availableDates, activeTab, availableTags, availableCategories, currentPage]);

	if (rendering)
		return (
			<div className="loader__container--agenda">
				<SkeletonLoader pageSize={config.pagination_pagesize} />
			</div>
		);

	return (
		<>
			<div className="agenda__header grid-item">
				<div className="agenda__header-container">
					<AgendaTabNav activeTab={activeTab} availableTabs={availableTabs} clickTabBar={clickTabBar} />

					<FilterbarMob
						memoizedActiveFilterDate={memoizedActiveFilterDate}
						memoizedActiveTimestamp={memoizedActiveTimestamp}
						memoizedDateRange={memoizedDateRange}
						memoizedActiveCategories={memoizedActiveCategories}
						memoizedActiveTags={memoizedActiveTags}
						activeTab={activeTab}
						availableDates={availableDates}
						availableCategories={availableCategories}
						availableTags={availableTags}
						setCategorySelected={setCategorySelected}
						categorySelected={categorySelected}
						setDateParam={setDateParam}
						setDateRangeParam={setDateRangeParam}
						setLocationsParam={setLocationsParam}
						setTagsParam={setTagsParam}
						setActiveTags={setActiveTags}
						setCategoriesParam={setCategoriesParam}
						resetParams={resetParams}
						showModal={showModal}
						setShowModal={setShowModal}
						currentPage={currentPage}
						setCurrentPage={setCurrentPage}
						filteredElements={filteredElements}
						headerRef={headerRef}
						scrollToTop={scrollToTop}
						setActiveTab={setActiveTab}
					/>
				</div>
			</div>
			<div className="grid-item" ref={headerRef}>
				<div className="agenda__display-tags-container">
					{memoizedActiveCategories.map((category, i) => (
						<button key={`category-${i}`} className="category btn tag--active" onClick={() => setCategoriesParam(category)}>
							<span className="category__text">{category}</span>
						</button>
					))}
					{memoizedActiveTags.map((tag, i) => {
						const formattedValue = Object.values(tag)[0];
						return (
							<button key={`tag-${i}`} className="tag btn tag--active" onClick={() => setActiveTags(memoizedActiveTags.filter((t) => Object.values(t)[0] !== formattedValue))}>
								<span className="tag__text">{formattedValue}</span>
							</button>
						);
					})}
				</div>

				{filteredElements.length < 1 ? (
					<EmptyAgenda text={config.empty_agenda_text} subtext={config.empty_agenda_subtext} />
				) : (
					<ol
						key={activeTab}
						className={`js_cards-container featured ${activeTab === 'series' ? 'series__tiles tiles' : 'agenda__tiles'} ${
							!rendering ? 'animated animated__fadeIn' : 'animated animated__preHidden'
						}`}
					>
						{activeTab === 'series'
							? paginatedElements.map((element, index) => <CardSeries key={index} {...element} toggleFavourite={toggleFavourite} allFavourites={allFavourites} />)
							: paginatedElements.map((element, index) => (
									<Card
										key={index}
										animationDelay={{ '--animation-delay': `${(index < 4 ? index + 1 : 4) * 100}ms` }}
										{...element}
										activeTab={activeTab}
										setTagsParam={setTagsParam}
										toggleFavourite={toggleFavourite}
										allFavourites={allFavourites}
									/>
							  ))}
					</ol>
				)}
			</div>
			<Pagination onPageChange={handlePageChange} totalCount={filteredElements.length} siblingCount={1} currentPage={currentPage} pageSize={config.pagination_pagesize} classNames={''} />
		</>
	);
};

export default React.memo(Filter);
